import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Action, Store } from '@ngrx/store';
import { current, produce } from 'immer';
import { concatMap, defer, EMPTY, finalize, map, Observable, of } from 'rxjs';

import {
  ApplyWorkflowNodePayload,
  runtimeActions,
  runtimeSelectors,
  RuntimeState,
} from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  findByRefIds,
  getNamespaceId,
  RuntimeUiEventPayload,
} from '@blaze-runtime/engine-domain';
import { BrandingTitleFormat, DatasetUpdateRequest } from '@blaze-shared/api-contracts-domain';
import { DatasetsApiService } from '@blaze-shared/datasets-data-access';
import {
  RenderElementDataRowContext,
  RuntimeContext,
  RuntimePage,
} from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { urlUtils } from '@blaze-shared/platform-util';
import {
  convertBlazeSchemaToElementSchema,
  DatasetRow,
  isElementSchema,
} from '@blaze-shared/schema-domain';
import { deepClone } from '@blaze-shared/shared-util';
import { BrandingInfo } from '@blaze-shared/theming-domain';
import { WorkflowsApiService } from '@blaze-shared/workflow-data-access';
import { Connection, Workflow } from '@blaze-shared/workflow-domain';

import { hasWrapperRefs } from '../../canvas-elements/element-model/element-props-guards';
import {
  isEditableForm,
  isElementType,
  isFormList,
  isWizard,
} from '../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { FormListRowsService } from '../../elements/form-list/form-list-rows.service';
import { getFormListDataRowById, getFormListIdColumn } from '../../utils/elements/form-list.utils';
import { getElementById } from '../elements/elements.selectors';

import { RuntimeFacade } from './runtime.facade';

type DryRunFormType = 'formContainer' | 'formDetails' | 'editableList';

@Injectable({ providedIn: 'root' })
export class RuntimeEffectsService {
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly formListRowsService = inject(FormListRowsService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly store = inject(Store<RuntimeState>);
  private readonly workflowsApiService = inject(WorkflowsApiService);

  findFormIdForWorkflow(
    wf: Workflow,
    connections: Connection[],
    entities: Dictionary<CanvasElement>,
  ): string | undefined {
    const validConnections = connections.filter(
      ({ source, target }) =>
        target.layerType === 'Workflow' && target.id === wf.id && source.layerType === 'Ui',
    );
    const clickConnection = validConnections.find(
      ({ source, eventType }) =>
        eventType === 'click' || (eventType === 'submit' && isWizard(entities[source.id])),
    );
    if (!clickConnection) {
      return undefined;
    }

    const submittableConnections = validConnections.filter(
      ({ source }) => isEditableForm(entities[source.id]) || isFormList(entities[source.id]),
    );
    if (!submittableConnections.length) {
      return undefined;
    }

    const clickForm = entities[clickConnection.source.id];
    if (submittableConnections.length === 1 || isWizard(clickForm)) {
      return submittableConnections[0].source.id;
    }

    // more than one form connected to the WF (BL-8404)
    const parentForm = submittableConnections.find((c) => {
      // find the form that commits with the clickConnection
      const form = entities[c.source.id];
      if (!hasWrapperRefs(form)) {
        return false;
      }
      const refs = getWrapperRefs(form);
      const commitRef = findByRefIds(refs, ['commit']);
      return commitRef === clickConnection.source.id;
    });
    return parentForm?.source.id;
  }

  /**
   * Injects Javascript into <head>
   * @param js
   * @param context
   */
  injectJavascriptAndHtml(js: string | undefined, context: Pick<RuntimeContext, '$$User'>): void {
    if (!js) {
      return;
    }
    const head = document.querySelector('head');
    const script = document.createElement('script');
    js = js.replaceAll(/<\/?script>/gi, '');
    const startupJs = js.startsWith('(function(')
      ? js
      : `(function(window, document){ ${js} })(window,document)`;

    const blazeContextJs: string[] = [];
    blazeContextJs.push('const blazeContext = {');
    blazeContextJs.push(`$$User: ${JSON.stringify(context.$$User || {})},`);
    blazeContextJs.push('};');

    script.innerHTML = [...blazeContextJs, startupJs].join('\n');
    head?.appendChild(script);
  }

  /**
   * Logs execution of a workflow node to the server.
   */
  logWorkflowNodeRunToServer(logInfo: ApplyWorkflowNodePayload): void {
    const { ctx, nodeInput, prevNodeId, schema, workflow, workflowNode } = logInfo;
    const workflowId = workflow?.id || '';
    const nodeId = workflowNode?.id || '';
    const log: any = {
      Node: nodeId,
      Input: nodeInput,
      'Input Schema': schema,
      'Previous Node': prevNodeId,
      Context: ctx,
    };
    if (workflowId) {
      this.workflowsApiService.postLog(workflowId, nodeId, 'Run workflow node', log).subscribe();
    } else {
      console.warn(`Unable to log to server. Workflow ID not specified.`);
    }
  }

  refreshFormulas(
    formElementId: CanvasElementId,
    inputElementId: CanvasElementId,
    columns: string[],
    idColumn: string,
    dataRow: DatasetRow,
    datasetId: string,
    rowContext: RenderElementDataRowContext | undefined,
  ): Observable<Action> {
    return defer(() => {
      this.runtimeFacade.updateDryRunStates(formElementId, true);
      return this.callDataRowDryRun(formElementId, datasetId, idColumn, dataRow).pipe(
        concatMap(({ updatedDataRow, formType }) => {
          const updateAction = this.getFormlikeOutputUpdateAction(
            formElementId,
            inputElementId,
            columns,
            updatedDataRow,
            formType,
            rowContext,
          );
          if (!updateAction) {
            return EMPTY;
          }
          return of(updateAction);
        }),
        finalize(() => {
          this.runtimeFacade.updateDryRunStates(formElementId, false);
        }),
      );
    });
  }

  /**
   * Based on branding, project and page information, sets browser tab title.
   */
  setTitleFromBranding(titleData: {
    branding: Partial<BrandingInfo>;
    currentPageId: string;
    pageId: string;
    pages: Dictionary<RuntimePage>;
    projectName?: string;
    tabTitleFormat?: BrandingTitleFormat;
  }): void {
    let { pageId, projectName } = titleData;
    const { branding, currentPageId, pages, tabTitleFormat } = titleData;

    projectName = projectName ?? branding.name;
    pageId = currentPageId || pageId;
    const pageName = pageId && pages ? pages[pageId]?.name : '';
    let title = 'Blaze';

    if (tabTitleFormat === BrandingTitleFormat.ProjectName && projectName) {
      // Project Name
      title = projectName;
    } else if (tabTitleFormat === BrandingTitleFormat.PageName && pageName) {
      // Page Name
      title = pageName;
    } else if (
      tabTitleFormat === BrandingTitleFormat.PageNameProjectName &&
      projectName &&
      pageName
    ) {
      // Page Name - Project Name
      title = `${pageName} - ${projectName}`;
    } else if (
      tabTitleFormat === BrandingTitleFormat.ProjectNamePageName &&
      projectName &&
      pageName
    ) {
      // Project Name - Page Name
      title = `${projectName} - ${pageName}`;
    } else if (urlUtils.currentUrlIsLoginRelated() && projectName) {
      title = projectName;
    }
    document.title = title;
  }

  private callDataRowDryRun(
    formElementId: CanvasElementId,
    datasetId: string,
    idColumn: string,
    dataRow: DatasetRow,
  ): Observable<{
    updatedDataRow: DatasetRow;
    formType: DryRunFormType;
  }> {
    const form = syncSelect(this.store, getElementById(formElementId));
    let isUpdate = true;
    let isUpsert = false;

    if (isElementType(form, 'formContainer') || isElementType(form, 'formDetails')) {
      const { action } = form.props.content;
      isUpdate = action === 'update' || (action === 'prompt' && !!idColumn && !!dataRow[idColumn]);
    } else if (isElementType(form, 'editableList')) {
      isUpsert = true;
    } else {
      throw new Error(`formula-refresh: not a form element: ${formElementId}`);
    }

    let apiCall$: Observable<DatasetRow>;
    if (!isUpdate) {
      apiCall$ = this.datasetsApiService.postInsert({
        datasetId,
        dryRun: true,
        includeRowId: true,
        row: dataRow,
      });
    } else {
      const updatePayload: DatasetUpdateRequest = {
        value: dataRow,
        rowId: dataRow[idColumn],
        upsert: isUpsert,
      };
      apiCall$ = this.datasetsApiService.postUpdate(datasetId, updatePayload, true, true);
    }
    return apiCall$.pipe(map((updatedDataRow) => ({ updatedDataRow, formType: form.type })));
  }

  private getFormlikeOutputUpdateAction(
    formElementId: CanvasElementId,
    inputElementId: CanvasElementId,
    columns: string[],
    updatedDataRow: DatasetRow,
    formType: DryRunFormType,
    rowContext: RenderElementDataRowContext | undefined,
  ): Action | undefined {
    const { data, schema } =
      syncSelect(
        this.store,
        runtimeSelectors.selectOutputUiDataWithSchemaById(formElementId, 'change'),
      ) ?? {};
    const elementSchema =
      schema && !isElementSchema(schema)
        ? convertBlazeSchemaToElementSchema(schema, formElementId, 'change')
        : schema;

    let isEmptyish = false;
    let emptyIshDataRowIdx = -1;

    const updatedData = produce(data as DatasetRow | DatasetRow[] | undefined, (draft) => {
      let dataRow = draft;
      const isFormList = formType === 'editableList';

      if (isFormList) {
        const { rowId } = rowContext ?? {};
        if (rowId === undefined) {
          console.log('refresh-formulas: missing row-context', {
            formElementId,
            inputElementId,
            columns,
            updatedDataRow,
            formType,
            rowContext,
          });
          return;
        }
        const idColumn = getFormListIdColumn(elementSchema);
        if (!idColumn) {
          console.log('refresh-formulas: missing id column', {
            formElementId,
            inputElementId,
            columns,
            updatedDataRow,
            formType,
            rowContext,
          });
          return;
        }
        const emptyishRows = this.formListRowsService.getFormListEmptyishRows(formElementId);
        const found = getFormListDataRowById(draft as DatasetRow[], emptyishRows, idColumn, rowId);
        if (found?.dataRow) {
          dataRow = found.dataRow;
          if (found.isEmptyish) {
            dataRow = deepClone(found.dataRow);
            isEmptyish = true;
            emptyIshDataRowIdx = found.dataRowIdx;
          }
        }
      }

      if (!dataRow) {
        return;
      }

      for (const column of columns) {
        if (column in updatedDataRow) {
          (dataRow as DatasetRow)[column] = updatedDataRow[column];
        }
      }

      if (isFormList) {
        if (isEmptyish) {
          this.formListRowsService.updateEmptyishRow(formElementId, emptyIshDataRowIdx, dataRow);
        } else {
          this.formListRowsService.updateValueDataRow(formElementId, current(dataRow));
        }
      }
    });

    if (!updatedData) {
      return;
    }
    const payload: RuntimeUiEventPayload = {
      data: updatedData,
      elementId: formElementId,
      eventType: 'change',
      isInitialData: false,
      ns: getNamespaceId(formElementId),
      schema: elementSchema,
      updateSource: `formulas-refresh:${inputElementId}`,
      rowContext,
    };
    return runtimeActions.runtimeUiEvent(payload);
  }
}
